import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createSelfOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to check
          in and support you as you get started with your Remote Patient
          Monitoring program. For quality and compliance purposes, this call may
          be recorded. To protect your privacy, can you please confirm your full
          name and date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'purposeOfCallExplained',
      type: 'radio',
      title: <p>Purpose of Call Explained</p>,
      description: (
        <ScriptWrapper>
          "This call is just to check in and make sure everything is going
          smoothly with your device setup."
          <br />
          "Many patients complete some or all of the setup on their own, and
          this is a chance to answer questions or help with anything that didn't
          go as planned."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Self-onboarding support purpose explained',
          value: 'selfOnboardingSupportPurposeExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends taking your readings as
          often as possible, ideally daily."
          <br />
          "Regular readings help your care team establish your normal range and
          notice changes early, even before symptoms show up."
          <br />
          "If you miss a day, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Daily readings encouraged',
          value: 'dailyReadingsEncouraged',
        },
        {
          key: makeid(10),
          label: 'Consistency emphasized',
          value: 'consistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'selfOnboardingStatusReviewed',
      type: 'radio',
      title: <p>Self-Onboarding Status Reviewed</p>,
      description: (
        <ScriptWrapper>
          "I want to check where you are in the setup process."
          <br />
          "Have you already set up your device and taken at least one reading?"
          <br />
          If YES: "That's great — nice work getting started."
          <br />
          If NO or PARTIAL: "That's completely okay — we can walk through any
          part together."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Device setup completed independently',
          value: 'deviceSetupCompletedIndependently',
        },
        {
          key: makeid(10),
          label: 'Partial setup completed',
          value: 'partialSetupCompleted',
        },
        {
          key: makeid(10),
          label: 'Setup not yet started',
          value: 'setupNotYetStarted',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceSetupReadingReview',
      type: 'radio',
      title: <p>Device Setup & Reading Review</p>,
      description: (
        <ScriptWrapper>
          "Which device or devices are you using as part of the program?"
          <br />
          (MA confirms device types and reviews status.)
          <br />
          "For each device you have, have you been able to take a reading
          successfully?"
          <br />
          If issues identified: "Thank you for letting me know — let's focus on
          that together."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading(s) completed successfully',
          value: 'readingsCompletedSuccessfully',
        },
        {
          key: makeid(10),
          label: 'Reading attempted but unsuccessful',
          value: 'readingAttemptedButUnsuccessful',
        },
        {
          key: makeid(10),
          label: 'Reading deferred',
          value: 'readingDeferred',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'techniqueReinforcementProvided',
      type: 'radio',
      title: <p>Technique Reinforcement Provided</p>,
      description: (
        <ScriptWrapper>
          "Sometimes small things like positioning, placement, or timing can
          affect readings."
          <br />
          "I can walk you through any steps again if that would be helpful."
          <br />
          (MA provides device-specific coaching only as needed.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Additional coaching provided',
          value: 'additionalCoachingProvided',
        },
        {
          key: makeid(10),
          label: 'No additional coaching needed',
          value: 'noAdditionalCoachingNeeded',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueReinforced',
      type: 'checkbox',
      title: <p>Program Value Reinforced</p>,
      description: (
        <ScriptWrapper>
          "Your readings are securely shared with your care team, who review
          trends over time."
          <br />
          "Even normal readings are important — they help show what's working
          and provide a baseline for comparison."
          <br />
          "If readings are missing for an extended period, you may receive
          reminder texts or a call from us — just to check in and make sure
          everything's okay."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Engagement encouraged',
          value: 'engagementEncouraged',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we currently have on file for
          you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
          <br />
          If Medicare Advantage: "Which plan is listed?"
          <br />
          "Thank you — I'll confirm what's documented in your chart."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
        {
          key: makeid(10),
          label: 'Insurance confirmed in chart',
          value: 'insuranceConfirmedInChart',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Next Monthly Check-In</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "If your weight changes outside of your provider's recommended range,
          your clinic may reach out — often with a call from a nurse — to decide
          if any follow-up is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Requirement reviewed',
          value: 'requirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Value explained',
          value: 'valueExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel comfortable using the device and
          understand what to expect moving forward?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time — you're officially set up, and we're glad to
          support you."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TSelfOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createSelfOnboardingTemplate>
>;

export const createSelfOnboardingBlurb = (
  data: TSelfOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    purposeOfCallExplained,
    earlyGoalSetting,
    selfOnboardingStatusReviewed,
    deviceSetupReadingReview,
    techniqueReinforcementProvided,
    programValueReinforced,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (purposeOfCallExplained && !hasField(ctx, 'purposeOfCallExplained')) {
    addField(ctx, 'purposeOfCallExplained');
    txt.push(
      `Self-onboarding support call purpose explained. Patient informed this call is to support device setup and answer questions.`,
    );
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');
    const map = {
      dailyReadingsEncouraged: 'Regular daily readings encouraged.',
      consistencyEmphasized: 'Consistency over time emphasized.',
    };
    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (
    selfOnboardingStatusReviewed &&
    !hasField(ctx, 'selfOnboardingStatusReviewed')
  ) {
    addField(ctx, 'selfOnboardingStatusReviewed');
    const map = {
      deviceSetupCompletedIndependently: 'completed',
      partialSetupCompleted: 'partially completed',
      setupNotYetStarted: 'not yet started',
    };
    txt.push(
      `Self-onboarding status reviewed. Patient reports ${map[selfOnboardingStatusReviewed]} device setup.`,
    );
  }

  if (deviceSetupReadingReview && !hasField(ctx, 'deviceSetupReadingReview')) {
    addField(ctx, 'deviceSetupReadingReview');
    const map = {
      readingsCompletedSuccessfully: 'completed',
      readingAttemptedButUnsuccessful: 'attempted but unsuccessful',
      readingDeferred: 'deferred',
    };
    txt.push(
      `Device setup and reading status reviewed. Reading(s) ${map[deviceSetupReadingReview]}.`,
    );
  }

  if (
    techniqueReinforcementProvided &&
    !hasField(ctx, 'techniqueReinforcementProvided')
  ) {
    addField(ctx, 'techniqueReinforcementProvided');
    const map = {
      additionalCoachingProvided: 'provided',
      noAdditionalCoachingNeeded: 'not required',
    };
    txt.push(
      `Device technique and setup reinforcement ${map[techniqueReinforcementProvided]}.`,
    );
  }

  if (
    programValueReinforced?.length &&
    !hasField(ctx, 'programValueReinforced')
  ) {
    addField(ctx, 'programValueReinforced');
    const map = {
      trendMonitoringExplained:
        'Program value reviewed, including baseline and trend monitoring.',
      engagementEncouraged: 'Patient engagement in monitoring encouraged.',
    };
    txt.push(...mapValuesToText(programValueReinforced, map));
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Insurance verified as Medicare.',
      medicareAdvantage: 'Insurance verified as Medicare Advantage.',
      commercial: 'Insurance verified as Commercial.',
      insuranceConfirmedInChart: 'Insurance info confirmed in chart.',
    };
    const line = map[insuranceVerificationOnFile];
    if (line) txt.push(line);
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      requirementReviewed: 'monthly phone call requirement',
      valueExplained: 'program monitoring process explained',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }
  return txt.join(' ');
};
